import type { SignalHttpCreateInputSchema } from './signals-http-create.schema';
import type { SignalUpdateInputSchema } from './signals-update.schema';
import type { HarEntry } from '@terra/shared-schemas-tracker';
import type { z } from 'zod';
import type {
  BulkDeleteSignalsPayloadSchema,
  BulkDeleteSignalsResponseSchema,
  BulkUpdateSignalsPayloadSchema,
  BulkUpdateSignalsResponseSchema,
  SignalQualityDistributionSchema,
  TopEndpointsBySignalsSchema,
  SignalsAggregatedCountReturnSchema,
  SignalsCountByApplicationReturnSchema,
  SignalsCountByApplicationBodySchema,
  SignalsCountByApplicationIdParamsSchema,
  SignalsCountByApplicationIdReturnSchema,
  SignalsCountByAssetReturnSchema,
  SignalsCountReturnSchema,
  SignalsSearchQuerySchema,
  SignalsSearchByApplicationBodySchema,
} from './signals.schema';
import { GroupedSignalSchema, type GroupedSignal } from './signals.schema';

// Request DTOs
export type SignalsBodyDto = z.infer<typeof SignalHttpCreateInputSchema>;
export type SignalsUpdateDto = z.infer<typeof SignalUpdateInputSchema>;
export type SignalsCountByApplicationIdParamsDto = z.infer<
  typeof SignalsCountByApplicationIdParamsSchema
>;
export type SignalsByApplicationIdParamsDto = z.infer<
  typeof SignalsCountByApplicationIdParamsSchema
>;
export type SignalsSearchQueryDto = z.infer<typeof SignalsSearchQuerySchema>;
export type SignalsSearchByApplicationBodyDto = z.infer<
  typeof SignalsSearchByApplicationBodySchema
>;
export type SignalsCountByApplicationQueryDto = z.infer<
  typeof SignalsCountByApplicationBodySchema
>;
export type SignalsCountByApplicationQueryInputDto = z.input<
  typeof SignalsCountByApplicationBodySchema
>;

// Response DTOs
export type SignalsCountByApplicationIdResponseDto = z.infer<
  typeof SignalsCountByApplicationIdReturnSchema
>;
export type SignalsCountByApplicationResponseDto = z.infer<
  typeof SignalsCountByApplicationReturnSchema
>;
export type SignalsCountResponseDto = z.infer<typeof SignalsCountReturnSchema>;
export type SignalsAggregatedCountResponseDto = z.infer<
  typeof SignalsAggregatedCountReturnSchema
>;
export type SignalsCountByAssetResponseDto = z.infer<
  typeof SignalsCountByAssetReturnSchema
>;

// Dashboard Analytics DTO
export type SignalQualityDistributionDto = z.infer<
  typeof SignalQualityDistributionSchema
>;
export type TopEndpointsBySignalsDto = z.infer<
  typeof TopEndpointsBySignalsSchema
>;

type HarEntryForSignal = HarEntry;

export type GetSignalResponseBody = z.infer<typeof GroupedSignalSchema>;

export type SignalDto = GetSignalResponseBody & {
  harEntry?: HarEntryForSignal | null;
  validationCodeLocations?: unknown;
};

export function toSignalDto(
  signal: z.input<typeof GroupedSignalSchema>,
): SignalDto {
  const parsed = GroupedSignalSchema.safeParse(signal);

  if (parsed.success) {
    return parsed.data;
  }

  return {
    ...(signal as GetSignalResponseBody),
    cweIds: signal.cweIds ?? [],
  };
}

export function normalizeGroupedSignal(
  signal: z.input<typeof GroupedSignalSchema>,
): GroupedSignal {
  const parsed = GroupedSignalSchema.safeParse(signal);

  if (parsed.success) {
    return parsed.data;
  }

  return {
    ...(signal as GroupedSignal),
    cweIds: signal.cweIds ?? [],
  };
}

// Signal update DTOs
export type BulkUpdateSignalsPayloadDto = z.infer<
  typeof BulkUpdateSignalsPayloadSchema
>;
export type BulkUpdateSignalsResponseDto = z.infer<
  typeof BulkUpdateSignalsResponseSchema
>;
export type BulkDeleteSignalsPayloadDto = z.infer<
  typeof BulkDeleteSignalsPayloadSchema
>;
export type BulkDeleteSignalsResponseDto = z.infer<
  typeof BulkDeleteSignalsResponseSchema
>;
